/**
 * Ordinary least-squares linear regression over two parallel numeric arrays.
 * Callers resolve the x/y columns (via `xColumn` and the y encoding) and pass
 * the already-coerced, aligned numeric values. Returns null when there aren't
 * enough points. Keeps the scatter trend line's behavior unchanged.
 */

export function linearRegression(
  xs: number[],
  ys: number[]
): { slope: number; intercept: number } | null {
  const n = Math.min(xs.length, ys.length);
  if (n < 2) return null;

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  for (let i = 0; i < n; i++) {
    const x = xs[i];
    const y = ys[i];
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  }

  const denom = n * sumXX - sumX * sumX;
  if (!Number.isFinite(denom) || Math.abs(denom) < 1e-12) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  if (!Number.isFinite(slope) || !Number.isFinite(intercept)) return null;
  return { slope, intercept };
}
